import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { ApiService } from 'merlin-library';
import { Mode, ServiceCode, ServiceName } from '../enums/clinic-admin.enum';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import {
    AssociatedClinicListData,
    ClinicProfileModel,
    LookupData,
    ClinicModel,
    AssociatedClinic,
} from '../models/clinic-profile-model';
import { ApiEndPointConstants } from 'merlin-core';
import { GridState } from '../data-table/associated-clinics-data';
import { HttpClient } from '@angular/common/http';
import { ClinicianApiEndPointConstants } from '../../utilitites/api-end-point-constants';

@Injectable({
    providedIn: 'root',
})
export class ClinicAdminProfileService {
    public mode$: Subject<Mode | string> = new Subject();
    public confirmPopup$: Subject<boolean> = new Subject();
    public clinicProfileDetails$: BehaviorSubject<ClinicProfileModel> =
        new BehaviorSubject(null);
    public token = localStorage.getItem('token');
    private removedClinics$: BehaviorSubject<AssociatedClinic[]> =
        new BehaviorSubject(null);
    isDirty: any = false;
    setRemovedClinics(clinics: any[]) {
        this.removedClinics$.next(clinics);
    }
    getRemovedClinics(): Observable<AssociatedClinic[]> {
        return this.removedClinics$.asObservable();
    }

    constructor(private apiService: ApiService, private http: HttpClient) {}

    getModeObs(): Observable<string | Mode> {
        return this.mode$.asObservable().pipe(
            distinctUntilChanged(this.comparer),
            switchMap((mode) => of(mode))
        );
    }

    setModeObs(mode: string | Mode) {
        this.mode$.next(mode);
    }

    comparer(pre, next): boolean {
        return pre === next;
    }

    getConfirmPopupObs(): Observable<boolean> {
        return this.confirmPopup$.asObservable();
    }

    setConfirmPopupObs(confirmPopup: boolean) {
        this.confirmPopup$.next(confirmPopup);
    }

    view(): Observable<ClinicProfileModel> {
        return this.apiService.get(
            ClinicianApiEndPointConstants.CLINIC_PROFILE_DETAILS
        );
    }

    update(data: ClinicProfileModel): Observable<ClinicProfileModel> {
        return this.apiService.put(
            ClinicianApiEndPointConstants.CLINIC_PROFILE_DETAILS,
            data
        );
    }

    getDataByServiceName(serviceName: ServiceName) {
        switch (serviceName) {
            case ServiceName.TimeZones:
                return this.apiService.get(
                    ApiEndPointConstants.qualifierUrl + ServiceCode.TimeZones
                ) as Observable<LookupData[]>;
            case ServiceName.DateFormats:
                return this.apiService.get(
                    ApiEndPointConstants.qualifierUrl + ServiceCode.DateFormats
                ) as Observable<LookupData[]>;
            case ServiceName.NumberFormats:
                return this.apiService.get(
                    ApiEndPointConstants.qualifierUrl +
                        ServiceCode.NumberFormats
                ) as Observable<LookupData[]>;
            case ServiceName.Languages:
                return this.apiService.get(
                    ApiEndPointConstants.qualifierUrl + ServiceCode.Languages
                ) as Observable<LookupData[]>;
            case ServiceName.TimeFormats:
                return this.apiService.get(
                    ApiEndPointConstants.qualifierUrl + ServiceCode.TimeFormats
                ) as Observable<LookupData[]>;
            case ServiceName.WeightUnits:
                return this.apiService.get(
                    ApiEndPointConstants.qualifierUrl + ServiceCode.WeightUnits
                ) as Observable<LookupData[]>;
            case ServiceName.ContactTypes:
                return this.apiService.get(
                    ApiEndPointConstants.qualifierUrl + ServiceCode.ContactTypes
                ) as Observable<LookupData[]>;
            case ServiceName.PasswordExpirationTypes:
                return this.apiService.get(
                    ApiEndPointConstants.qualifierUrl +
                        ServiceCode.PasswordExpirationTypes
                ) as Observable<LookupData[]>;
            case ServiceName.States:
                return this.apiService.get(
                    ApiEndPointConstants.qualifierUrl + ServiceCode.States
                ) as Observable<LookupData[]>;
            case ServiceName.Countries:
                return this.apiService.get(
                    ApiEndPointConstants.qualifierUrl + ServiceCode.Countries
                ) as Observable<LookupData[]>;
            default:
                break;
        }
    }

    // TODO: Need to test this.. looks like this is not being used.. and URL is also wrong!
    public getAssociatedClinicList(
        gridState: GridState
    ): Observable<AssociatedClinicListData> {
        const url = '';
        return this.apiService.get(
            ApiEndPointConstants.qualifierUrl + ServiceCode.AssociatedClinics
        ) as Observable<AssociatedClinicListData>;
    }

    getLookupDataByNameOrId(value: string): Observable<LookupData> {
        return this.apiService.get(ApiEndPointConstants.lookupDataUrl + value);
    }

    viewTransmitteronDemand(): Observable<ClinicProfileModel> {
        return this.apiService.get(
            ClinicianApiEndPointConstants.CLINIC_PROFILE_DETAILS
        );
    }
    getClinicProfileDetailsObs() {
        return this.clinicProfileDetails$.asObservable();
    }
    setClinicProfileDetailsObs(clinicProfileDetails: ClinicProfileModel) {
        this.clinicProfileDetails$.next(clinicProfileDetails);
    }
    getAddClinic(searchVal): Observable<any> {
        // TODO: Not in use.. need to check this..
        return this.apiService.get(
            `${ClinicianApiEndPointConstants.findClinics}?${searchVal}`
        );
    }

    searchClinic(searchVal): Observable<any> {
        return this.apiService.get(
            ClinicianApiEndPointConstants.clinicProfileFinder(searchVal)
        );
    }

    getPatientList(cfIds, acIdsExisting) {
        // TODO: Not in use.. need to check this..
        return this.apiService.get(
            `${ClinicianApiEndPointConstants.loadAssociatedClinics}?cfIds=${cfIds}&acIdsExisting=${acIdsExisting}`
        );
    }
}
